/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from 'react';
import { filesApi } from '@/api/files.api';
import type { SortingState } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { FileFilters } from '../types/file.types';
import type { File } from '../types/file.types';
import { FILES_QUERY_KEYS } from '../constants/queryKeys';
import { useAppSelector } from '@/store/hooks';
import toast from 'react-hot-toast';

interface UseFilesDragAndDropReturn {
  isReordering: boolean;
  handleDragEnd: (
    activeId: string,
    overId: string | null,
    items: string[]
  ) => Promise<void>;
  isDragEnabled: boolean;
}

interface UseFilesDragAndDropProps {
  sorting: SortingState;
  search: string;
  onlyMe: boolean;
  filters: FileFilters[];
  onResetSelection?: () => void;
}

const calculateRelativeOrder = (
  beforeOrder: number,
  afterOrder: number
): number => {
  const difference = afterOrder - beforeOrder;

  if (difference >= 1) {
    return beforeOrder + Math.floor(difference / 2);
  }

  if (difference >= 0.1) {
    return beforeOrder + difference / 2;
  }

  if (difference >= 0.01) {
    return beforeOrder + difference / 2;
  }

  return beforeOrder + difference / 2;
};

export const useFilesDragAndDrop = ({
  sorting,
  search,
  onlyMe,
  filters,
  onResetSelection,
}: UseFilesDragAndDropProps): UseFilesDragAndDropReturn => {
  const [isReordering, setIsReordering] = useState(false);
  const queryClient = useQueryClient();

  const { currentPage, pageSize } = useAppSelector((state) => state.files);

  const isDragEnabled =
    sorting.length === 0 && search === '' && filters.length === 0;

  const handleDragEnd = useCallback(
    async (activeId: string, overId: string | null, items: string[]) => {
      if (!overId || activeId === overId || !isDragEnabled) {
        return;
      }
      if (onResetSelection) {
        onResetSelection();
      }

      setIsReordering(true);

      const queryParams = {
        page: currentPage,
        pageSize: pageSize,
        sortBy: sorting[0]?.id || 'order_nb',
        sortOrder:
          sorting[0] && !sorting[0]?.desc
            ? ('asc' as const)
            : ('desc' as const),
        search,
        onlyMe,
        filters,
      };

      const queryKey = FILES_QUERY_KEYS.LIST(queryParams);

      const files =
        (queryClient.getQueryData(queryKey) as { documents: File[] })
          ?.documents || [];

      const previousData = queryClient.getQueryData(queryKey);

      try {
        const oldIndex = items.indexOf(activeId);
        const newIndex = items.indexOf(overId);
        let newOrder: number;

        const fileMap = new Map<string, File>();
        files.forEach((file: File) => {
          fileMap.set(file.id, file);
        });

        const isMovingUp = oldIndex > newIndex;

        if (newIndex === 0) {
          // Déplacé au début
          const allOrders = files
            .map((f) => f.order_nb || 0)
            .filter((o) => o > 0);
          const maxOrder = allOrders.length > 0 ? Math.max(...allOrders) : 0;
          newOrder = maxOrder + 0.01;
        } else if (newIndex === items.length - 1) {
          // Déplacé à la fin
          const allOrders = files
            .map((f) => f.order_nb || 0)
            .filter((o) => o > 0);
          const minOrder = allOrders.length > 0 ? Math.min(...allOrders) : 0;
          newOrder = minOrder - 0.01;
        } else {
          // Déplacé entre deux éléments
          let beforeFile: File | undefined;
          let afterFile: File | undefined;

          if (isMovingUp) {
            // On monte : on veut être entre l'élément au-dessus et l'élément actuel
            beforeFile = fileMap.get(items[newIndex - 1]);
            afterFile = fileMap.get(items[newIndex]);
          } else {
            // On descend : on veut être entre l'élément actuel et l'élément en-dessous
            beforeFile = fileMap.get(items[newIndex]);
            afterFile = fileMap.get(items[newIndex + 1]);
          }

          const beforeOrder = beforeFile?.order_nb || 0;
          const afterOrder = afterFile?.order_nb || 0;

          newOrder = calculateRelativeOrder(beforeOrder, afterOrder);
        }

        // Mise à jour optimiste : on met à jour l'interface immédiatement
        await queryClient.setQueryData(queryKey, (oldData: any) => {
          if (!oldData?.documents) return oldData;

          const updatedDocuments = (oldData.documents as File[])
            .map((file: File) =>
              file.id === activeId ? { ...file, order_nb: newOrder } : file
            )
            .sort((a, b) => (b.order_nb || 0) - (a.order_nb || 0));

          return {
            ...oldData,
            documents: updatedDocuments,
            _lastUpdated: Date.now(),
          };
        });

        await filesApi.updateFileOrder(activeId, newOrder);

        queryClient.invalidateQueries({ queryKey });
      } catch (error) {
        toast.error("Erreur lors de la mise à jour de l'ordre:");

        if (previousData) {
          queryClient.setQueryData(queryKey, previousData);
        }
      } finally {
        setIsReordering(false);
      }
    },
    [
      isDragEnabled,
      queryClient,
      currentPage,
      pageSize,
      sorting,
      search,
      onlyMe,
      filters,
      onResetSelection,
    ]
  );

  return {
    isReordering,
    handleDragEnd,
    isDragEnabled,
  };
};
